﻿import * as THREE from 'three';
import { advanceHarpoonFrame, fireHarpoon, retrieveHarpoon, setHarpoonAutoRetractCallback, setHarpoonRockRetrievedCallback, updateHarpoonAnchorPosition } from './HarpoonSceneManager.js';
import { WorldViewportView } from './WorldViewportView.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from './renderTuning.js';
import type { GraphicsQuality } from './quality.js';
import type { GameSnapshot, RoomState } from '../../shared/types.js';
import { simulationToMapPoint } from '../../shared/spatial.js';
import type { RenderingIntent } from '../../shared/renderingIntent.js';
import type { ViewportCameraPolicy } from './hostCameraPolicy.js';
import { type BeamGlowState } from './hostWorldSceneLighting.js';
import { createCameraSpotlightVolumetricCone, updateCameraSpotlightVolumetricUniforms } from '../effects/volumetricLighting.js';

// Bottom-hull mount: centred left-to-right (X = 0), sitting on the bottom surface of the
// submarine's bounding box, and slightly forward of centre toward the bow. The mount is
// derived from the live model bounding box (submarineViewRoot-local space) so it tracks the
// actual hull. The sub-local offset is rotated with the submarine's yaw each frame so the
// camera stays glued to the hull when the sub turns.
//
// Forward-of-centre amount, as a fraction of the hull's half-length toward the bow (−Z).
const HARPOON_MOUNT_FORWARD_FRACTION = 0.3;
// Fallback mount, used only until the model bounding box has been measured.
// Fixed under-bow values, independent of hullCameraPosition (which mounts the
// main view on the front face of the conning tower).
const HARPOON_FALLBACK_Y = -0.57;
const HARPOON_FALLBACK_Z = -1.42;

/**
 * Harpoon spotlight parameters.
 *
 * Intensity: 75% of the default hullLightBrightness reference (16 × 0.75 = 12).
 * This makes the harpoon beam noticeably bright while remaining subordinate to the
 * main periscope spotlight in shared scenes.
 *
 * Angle / penumbra: matched to the periscope spotlight at focus = 3.5.
 *   focusNarrowness = (3.5 − 0.2) / 3.8 ≈ 0.868
 *   angle   = lerp(π × 0.32, π × 0.08, 0.868) ≈ π × 0.112  (~20°)
 *   penumbra = lerp(0.92, 0.28, 0.868) ≈ 0.36
 *
 * Decay = 2 gives physically-correct inverse-square distance falloff.
 */
const HARPOON_LIGHT_INTENSITY = 24.0;
const HARPOON_LIGHT_DISTANCE = 30;
const HARPOON_LIGHT_ANGLE = Math.PI * 0.112; // periscope focus 3.5 equivalent (~20°)
const HARPOON_LIGHT_PENUMBRA = 0.36;
const HARPOON_LIGHT_DECAY = 2;
const HARPOON_SCAN_VISUAL_RANGE_METERS = 35;
const HARPOON_SCAN_DURATION_MS = 10000;
const HARPOON_SCAN_LAYER = 11;

const createHarpoonCameraPolicy = (
  getYawOffset: () => number,
  getPitchOffset: () => number,
  getSubmarineBounds: () => THREE.Box3 | null
): ViewportCameraPolicy => {
  const setCameraTransform = (
    camera: THREE.PerspectiveCamera,
    submarineYawRadians: number,
    renderIntent: RenderingIntent,
    submarinePitchRadians = 0,
    submarineRollRadians = 0,
    hullSeat?: { x: number; y: number; z: number }
  ): void => {
    // Mount position in submarine-local space. Prefer the live model bounding box so the
    // camera sits on the actual bottom surface, centred X and slightly toward the bow.
    const bounds = getSubmarineBounds();
    const localX = 0; // centred left-to-right
    let localY: number;
    let localZ: number;
    if (bounds) {
      localY = bounds.min.y; // bottom surface of the bounding box
      const halfLength = (bounds.max.z - bounds.min.z) / 2;
      localZ = -halfLength * HARPOON_MOUNT_FORWARD_FRACTION; // slightly toward the bow (−Z)
    } else {
      localY = HARPOON_FALLBACK_Y;
      localZ = HARPOON_FALLBACK_Z;
    }

    // Rotate the submarine-local offset into world space using the submarine's yaw,
    // matching the periscope camera pattern so the mount stays glued to the hull.
    // Rotate by the full hull attitude (same 'YXZ' Euler the hull model uses; its X rotation is
    // -pitchRadians) so the mount stays on the keel as the sub pitches and rolls.
    // While docked the hull settles onto the docking pad (dockSeatOffset, a world-space offset), and the
    // mount has to travel with it - otherwise the camera floats high above the sub.
    camera.position.set(localX, localY, localZ)
      .applyEuler(new THREE.Euler(-submarinePitchRadians, submarineYawRadians, submarineRollRadians, 'YXZ'))
      .add(new THREE.Vector3(hullSeat?.x ?? 0, hullSeat?.y ?? 0, hullSeat?.z ?? 0));

    const totalYaw = submarineYawRadians + getYawOffset();
    // submarinePitchRadians is negative for nose-up; camera X rotation is positive-up, so negate it.
    const totalPitch = getPitchOffset() - submarinePitchRadians;
    camera.setRotationFromEuler(new THREE.Euler(totalPitch, totalYaw, submarineRollRadians, 'YXZ'));
    camera.up.set(0, 1, 0);
  };

  return {
    initializeCamera: (camera: THREE.PerspectiveCamera, renderIntent: RenderingIntent) => {
      setCameraTransform(camera, 0, renderIntent);
    },
    applyOrientation: ({ yawRadians, pitchRadians, rollRadians, camera, renderIntent, dockedOrbitCenter }: { yawRadians: number; pitchRadians: number; rollRadians: number; camera: THREE.PerspectiveCamera; renderIntent: RenderingIntent } & any) => {
      setCameraTransform(camera, yawRadians, renderIntent, pitchRadians ?? 0, rollRadians ?? 0, dockedOrbitCenter);
    },
    applyShake: () => 0
  };
};

export class HarpoonCameraView {
  private readonly viewportView: WorldViewportView;
  private readonly lightTarget: THREE.Object3D;
  private readonly spotLight: THREE.SpotLight;
  private readonly glowLight: THREE.PointLight;
  private readonly scanHighlightGroup: THREE.Group;
  private readonly scanFrustum = new THREE.Frustum();
  private readonly scanMarkers: Array<{ object: THREE.Object3D; geometry: THREE.BufferGeometry; material: THREE.Material; label?: THREE.Sprite; labelMaterial?: THREE.SpriteMaterial; labelTexture?: THREE.CanvasTexture; connector?: THREE.LineSegments; connectorMaterial?: THREE.LineBasicMaterial; connectorGeometry?: THREE.BufferGeometry; startMs: number; durationMs: number; baseScale: number }> = [];
  private lastSnapshot: GameSnapshot | null = null;
  private engineOverloaded = false;
  private yawOffset = 0;
  private pitchOffset = 0;
  // Volumetric cone (3-D water scatter).
  private readonly volumetricMesh: THREE.Mesh;
  private readonly volumetricMaterial: THREE.ShaderMaterial;
  // Glow state updated each frame and registered with the particle system so particles
  // within the harpoon beam cone receive illumination.
  private readonly harpoonGlowState: BeamGlowState = {
    source: new THREE.Vector3(),
    direction: new THREE.Vector3(0, 0, -1),
    angle: HARPOON_LIGHT_ANGLE,
    range: HARPOON_LIGHT_DISTANCE,
    intensity: 0
  };
  private readonly tempVec = new THREE.Vector3();

  constructor(
    container: HTMLElement,
    quality: GraphicsQuality,
    tuning: RenderTuning = DEFAULT_RENDER_TUNING,
    fpsOverride?: number
  ) {
    this.viewportView = new WorldViewportView(
      container,
      quality,
      tuning,
      createHarpoonCameraPolicy(
        () => this.yawOffset,
        () => this.pitchOffset,
        // Guarded: initializeCamera runs during the WorldViewportView constructor below,
        // before this.viewportView has been assigned, so fall back to null in that window.
        () => this.viewportView?.getSubmarineModelLocalBounds() ?? null
      ),
      undefined,
      {
        autoStartAnimation: true,
        fpsOverride
      }
    );
    this.viewportView.setPeriscopeSubmarineVisible(true);

    const camera = this.viewportView.getCamera();
    camera.layers.enable(HARPOON_SCAN_LAYER);
    this.lightTarget = new THREE.Object3D();
    this.lightTarget.position.set(0, 0, -5);
    camera.add(this.lightTarget);

    this.spotLight = new THREE.SpotLight(0x77d6ff, HARPOON_LIGHT_INTENSITY, HARPOON_LIGHT_DISTANCE, HARPOON_LIGHT_ANGLE, HARPOON_LIGHT_PENUMBRA, HARPOON_LIGHT_DECAY);
    this.spotLight.position.set(0, 0, 0.14);
    this.spotLight.target = this.lightTarget;
    this.spotLight.penumbra = HARPOON_LIGHT_PENUMBRA;
    this.spotLight.decay = HARPOON_LIGHT_DECAY;
    this.spotLight.castShadow = false;

    this.glowLight = new THREE.PointLight(0x77d6ff, 0, 5, 2);
    this.glowLight.position.set(0, 0, 0.14);
    this.glowLight.castShadow = false;

    camera.add(this.spotLight);
    camera.add(this.glowLight);

    // Volumetric cone: 3-D scattered-light volume for the "God ray" effect.
    const volResult = createCameraSpotlightVolumetricCone(
      new THREE.Color(0x77d6ff), HARPOON_LIGHT_ANGLE, HARPOON_LIGHT_DISTANCE, 100
    );
    this.volumetricMesh = volResult.mesh;
    this.volumetricMaterial = volResult.material;
    this.viewportView.getScene().add(this.volumetricMesh);

    // Register the harpoon spotlight as a particle glow source.
    this.viewportView.setExternalParticleGlowSources([this.harpoonGlowState]);

    this.scanHighlightGroup = new THREE.Group();
    this.viewportView.getScene().add(this.scanHighlightGroup);
  }

  fireHarpoon(): boolean {
    const camera = this.viewportView.getCamera();
    const startPosition = new THREE.Vector3();
    camera.getWorldPosition(startPosition);
    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward).normalize();
    startPosition.add(forward.clone().multiplyScalar(0.35));

    const anchorPosition = new THREE.Vector3();
    camera.getWorldPosition(anchorPosition);

    return fireHarpoon(startPosition, camera.quaternion, anchorPosition);
  }

  retrieveHarpoon(): void {
    const camera = this.viewportView.getCamera();
    const currentPosition = new THREE.Vector3();
    camera.getWorldPosition(currentPosition);
    retrieveHarpoon(currentPosition);
  }

  setRockRetrievedCallback(callback: ((payload: { resourceType: string; resourceValue: number }) => void) | null): void {
    setHarpoonRockRetrievedCallback(callback);
  }

  setSnapshot(snapshot: GameSnapshot): void {
    this.lastSnapshot = snapshot;
    this.viewportView.setSnapshot(snapshot);
    this.engineOverloaded = snapshot.submarine.engineOverloaded;
    const enabled = snapshot.submarine.miningLightsOn && !snapshot.submarine.engineOverloaded;
    this.spotLight.intensity = enabled ? HARPOON_LIGHT_INTENSITY : 0;
    this.glowLight.intensity = enabled ? 0.92 : 0;
  }

  triggerScan(snapshot: GameSnapshot): void {
    const camera = this.viewportView.getCamera();
    camera.updateMatrixWorld(true);
    const cameraPosition = new THREE.Vector3();
    camera.getWorldPosition(cameraPosition);
    const matrixWorldInverse = new THREE.Matrix4().copy(camera.matrixWorld).invert();
    const projectionMatrix = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, matrixWorldInverse);
    this.scanFrustum.setFromProjectionMatrix(projectionMatrix);
    const now = performance.now();

    const highlightedPoints: THREE.Vector3[] = [];
    const addScanHighlight = (resourcePoint: THREE.Vector3, resourceValue: number, resourceType?: string, sourceObject?: THREE.Object3D): void => {
      const alreadyTracked = highlightedPoints.some((point) => point.distanceToSquared(resourcePoint) < 1);
      if (alreadyTracked) {
        return;
      }

      highlightedPoints.push(resourcePoint.clone());

      let wireGeometry: THREE.BufferGeometry;
      let highlightPosition: THREE.Vector3;
      let baseScale: number;

      if (sourceObject) {
        const box = new THREE.Box3().setFromObject(sourceObject);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const boxGeo = new THREE.BoxGeometry(size.x, size.y, size.z);
        wireGeometry = new THREE.WireframeGeometry(boxGeo);
        boxGeo.dispose();
        highlightPosition = center;
        baseScale = 1.0;
      } else {
        const sphereGeo = new THREE.SphereGeometry(0.32 * (1.25 + Math.min(2.75, resourceValue / 8)), 10, 7);
        wireGeometry = new THREE.WireframeGeometry(sphereGeo);
        sphereGeo.dispose();
        highlightPosition = resourcePoint;
        baseScale = 1.0;
      }

      const wireMaterial = new THREE.LineBasicMaterial({
        color: 0x7ee8ff,
        transparent: true,
        opacity: 0.96,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        toneMapped: false
      });
      const highlight = new THREE.LineSegments(wireGeometry, wireMaterial);
      highlight.position.copy(highlightPosition);
      highlight.scale.setScalar(baseScale);
      highlight.layers.set(HARPOON_SCAN_LAYER);
      highlight.renderOrder = 999;
      this.scanHighlightGroup.add(highlight);

      // Build a canvas label for the resource type and quantity.
      let label: THREE.Sprite | undefined;
      let labelMaterial: THREE.SpriteMaterial | undefined;
      let labelTexture: THREE.CanvasTexture | undefined;
      let connector: THREE.LineSegments | undefined;
      let connectorMaterial: THREE.LineBasicMaterial | undefined;
      let connectorGeometry: THREE.BufferGeometry | undefined;
      if (resourceType) {
        const labelText = `${resourceType}  ×${Math.round(resourceValue)}`;
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 128;
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = 'bold 48px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const metrics = ctx.measureText(labelText);
        const pad = 18;
        const bw = metrics.width + pad * 2;
        const bx = canvas.width / 2 - bw / 2;
        ctx.fillStyle = 'rgba(0,20,30,0.65)';
        ctx.beginPath();
        ctx.roundRect(bx, 20, bw, 88, 10);
        ctx.fill();
        ctx.fillStyle = '#7ee8ff';
        ctx.fillText(labelText, canvas.width / 2, canvas.height / 2);

        labelTexture = new THREE.CanvasTexture(canvas);
        labelMaterial = new THREE.SpriteMaterial({
          map: labelTexture,
          transparent: true,
          opacity: 0.96,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
          toneMapped: false
        });
        label = new THREE.Sprite(labelMaterial);
        // Offset label up and to the right of the highlight.
        const wireRadius = sourceObject
          ? (() => { const b = new THREE.Box3().setFromObject(sourceObject); return Math.max(b.max.x - b.min.x, b.max.y - b.min.y) * 0.5; })()
          : 0.32 * (1.25 + Math.min(2.75, resourceValue / 8));
        const labelPos = highlightPosition.clone().add(new THREE.Vector3(wireRadius + 1.2, wireRadius + 1.0, 0));
        label.position.copy(labelPos);
        // Scale in world units: canvas is 512×128, display at ~3×0.75 world units.
        label.scale.set(3.0, 0.75, 1.0);
        label.layers.set(HARPOON_SCAN_LAYER);
        label.renderOrder = 1000;
        this.scanHighlightGroup.add(label);

        // Connector line from the highlight surface to the bottom-left of the label.
        const lineStart = highlightPosition.clone().add(new THREE.Vector3(wireRadius * 0.7, wireRadius * 0.7, 0));
        const lineEnd = labelPos.clone().add(new THREE.Vector3(-1.5, -0.375, 0));
        connectorGeometry = new THREE.BufferGeometry().setFromPoints([lineStart, lineEnd]);
        connectorMaterial = new THREE.LineBasicMaterial({
          color: 0x7ee8ff,
          transparent: true,
          opacity: 0.6,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
          toneMapped: false
        });
        connector = new THREE.LineSegments(connectorGeometry, connectorMaterial);
        connector.layers.set(HARPOON_SCAN_LAYER);
        connector.renderOrder = 999;
        this.scanHighlightGroup.add(connector);
      }

      this.scanMarkers.push({ object: highlight, geometry: wireGeometry, material: wireMaterial, label, labelMaterial, labelTexture, connector, connectorMaterial, connectorGeometry, startMs: now, durationMs: HARPOON_SCAN_DURATION_MS, baseScale });
    };

    for (const node of snapshot.world.resourceNodes) {
      if (node.resourceValue <= 0) {
        continue;
      }
      const resourceMapPoint = simulationToMapPoint({ x: node.simulationX, y: node.simulationZ });
      const resourcePoint = new THREE.Vector3(resourceMapPoint.x, -node.depth, resourceMapPoint.z);
      const resourceRadius = 1;
      const distance = resourcePoint.distanceTo(cameraPosition);
      if (distance > HARPOON_SCAN_VISUAL_RANGE_METERS + resourceRadius) {
        continue;
      }
      const nodeSphere = new THREE.Sphere(resourcePoint, resourceRadius);
      if (!this.scanFrustum.intersectsSphere(nodeSphere)) {
        continue;
      }
      addScanHighlight(resourcePoint, node.resourceValue, node.resourceType);
    }

    this.viewportView.getScene().traverse((object) => {
      const markerMetadata = object.userData?.markerMetadata as Record<string, unknown> | undefined;
      if (!markerMetadata || typeof markerMetadata.resourceValue !== 'number' || markerMetadata.resourceValue <= 0) {
        return;
      }

      const resourcePoint = new THREE.Vector3();
      object.getWorldPosition(resourcePoint);
      const resourceRadius = 1;
      const distance = resourcePoint.distanceTo(cameraPosition);
      if (distance > HARPOON_SCAN_VISUAL_RANGE_METERS + resourceRadius) {
        return;
      }

      const nodeSphere = new THREE.Sphere(resourcePoint, resourceRadius);
      if (!this.scanFrustum.intersectsSphere(nodeSphere)) {
        return;
      }

      const rType = typeof markerMetadata.resourceType === 'string' ? markerMetadata.resourceType : undefined;
      addScanHighlight(resourcePoint, markerMetadata.resourceValue, rType, object);
    });
  }

  setYawPitch(yawDegrees: number, pitchDegrees: number): void {
    this.yawOffset = THREE.MathUtils.degToRad(yawDegrees);
    this.pitchOffset = THREE.MathUtils.degToRad(pitchDegrees);
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.viewportView.getCamera();
  }

  setZoom(fovDegrees: number): void {
    const camera = this.viewportView.getCamera();
    camera.fov = THREE.MathUtils.clamp(fovDegrees, 36, 80);
    camera.updateProjectionMatrix();
  }

  advanceFrame(timestampMs: number): void {
    const camera = this.viewportView.getCamera();
    const anchorPosition = new THREE.Vector3();
    camera.getWorldPosition(anchorPosition);
    updateHarpoonAnchorPosition(anchorPosition);

    advanceHarpoonFrame(timestampMs);
    this.viewportView.advanceFrame(timestampMs);

    const lightWorldPos = this.spotLight.getWorldPosition(this.tempVec.clone());
    const targetWorldPos = this.lightTarget.getWorldPosition(this.tempVec);
    const visibility = this.lastSnapshot?.world.visibility ?? 0.5;
    const elapsed = timestampMs / 1000;
    const lightOn = this.spotLight.intensity > 0;

    // Volumetric cone: 3-D god-ray scatter effect.
    this.volumetricMesh.visible = lightOn;
    if (lightOn) {
      this.volumetricMesh.position.copy(lightWorldPos);
      this.volumetricMesh.lookAt(targetWorldPos);
      updateCameraSpotlightVolumetricUniforms(
        this.volumetricMaterial,
        this.viewportView.getCamera(),
        visibility, elapsed,
        lightWorldPos, targetWorldPos,
        this.spotLight.intensity
      );
    }

    // Update particle glow state so particles within the cone are lit.
    this.spotLight.getWorldPosition(this.harpoonGlowState.source);
    this.lightTarget.getWorldPosition(this.tempVec);
    this.harpoonGlowState.direction.copy(this.tempVec).sub(this.harpoonGlowState.source).normalize();
    this.harpoonGlowState.angle = HARPOON_LIGHT_ANGLE;
    this.harpoonGlowState.range = HARPOON_LIGHT_DISTANCE;
    // Scale intensity for particle glow (spotlight intensity is much higher than glow headroom).
    this.harpoonGlowState.intensity = lightOn ? THREE.MathUtils.clamp(this.spotLight.intensity * 0.08, 0, 1.2) : 0;

    const remainingMarkers: typeof this.scanMarkers = [];
    for (const marker of this.scanMarkers) {
      const elapsed = timestampMs - marker.startMs;
      if (elapsed >= marker.durationMs) {
        this.scanHighlightGroup.remove(marker.object);
        marker.geometry.dispose();
        marker.material.dispose();
        if (marker.label) {
          this.scanHighlightGroup.remove(marker.label);
          marker.labelMaterial?.dispose();
          marker.labelTexture?.dispose();
        }
        if (marker.connector) {
          this.scanHighlightGroup.remove(marker.connector);
          marker.connectorMaterial?.dispose();
          marker.connectorGeometry?.dispose();
        }
        continue;
      }
      const fade = 1 - elapsed / marker.durationMs;
      marker.material.opacity = THREE.MathUtils.clamp(0.35 + fade * 0.5, 0, 0.85);
      const scale = marker.baseScale * (1 + 0.12 * fade);
      marker.object.scale.setScalar(scale);
      if (marker.labelMaterial) {
        marker.labelMaterial.opacity = THREE.MathUtils.clamp(fade * 0.96, 0, 0.96);
      }
      if (marker.connectorMaterial) {
        marker.connectorMaterial.opacity = THREE.MathUtils.clamp(fade * 0.6, 0, 0.6);
      }
      remainingMarkers.push(marker);
    }
    this.scanMarkers.length = 0;
    this.scanMarkers.push(...remainingMarkers);

    if (this.engineOverloaded) {
      const flicker = Math.max(0, Math.sin(timestampMs * 0.04) * 0.5 + 0.5);
      this.spotLight.intensity = flicker * HARPOON_LIGHT_INTENSITY * 0.72;
      this.glowLight.intensity = flicker * 0.92 * 0.72;
    }
  }

  setRoomState(state: RoomState): void {
    this.viewportView.setRoomState(state);
  }

  setAutoRetractCallback(callback: () => void): void {
    setHarpoonAutoRetractCallback(callback);
  }

  setRenderTuning(tuning: RenderTuning): void {
    this.viewportView.setRenderTuning(tuning);
  }

  triggerCollisionCloud(strength: number, simulationX: number, simulationZ: number): void {
    this.viewportView.triggerCollisionCloud(strength, simulationX, simulationZ);
  }

  dispose(): void {
    this.viewportView.dispose();
  }
}

